import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import { isQ7_1Complete } from '@/lib/q7_1Storage';
import { isQ7_2StepComplete } from '@/lib/q7_2Storage';
import { isQ7_3Complete } from '@/lib/q7_3Storage';
import { isQ7_4CompleteForSurvey } from '@/lib/q7_4Storage';
import { isQ7_5Complete } from '@/lib/q7_5Storage';
import { isQ7_6Complete } from '@/lib/q7_6Storage';
import {
  type DevRegAnswer,
  readDevRegAnswers,
  writeDevRegAnswers,
} from '@/lib/devregStorage';
import { isSmokerProfileComplete } from '@/lib/smokerProfileStorage';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSelector } from '@/stores/hooks';
import {
  selectCodeBrand,
  selectPdpaConsent,
  selectSurveyScreening,
  selectUniqueCodeDraft,
} from '@/stores/selectors';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

// Options are built dynamically in the component using t, so this array is no longer used directly.
type DevRegOption = { id: DevRegAnswer; labelKey: 'optionYes' | 'optionCareContact' | 'optionNoRegister' };
const OPTION_KEYS: DevRegOption[] = [
  { id: 'yes', labelKey: 'optionYes' },
  { id: 'care_contact', labelKey: 'optionCareContact' },
  { id: 'no_register', labelKey: 'optionNoRegister' },
];

const checkboxLabelClass =
  'flex cursor-pointer items-start gap-3 text-left text-[15px] font-medium leading-snug text-white/95 sm:text-[16px]';

const checkboxClass =
  'mt-0.5 h-[18px] w-[18px] shrink-0 rounded border border-slate-300/90 bg-white text-amber-800 focus:ring-1 focus:ring-slate-300';

export default function DevReg() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const codeBrand = useAppSelector(selectCodeBrand);
  const { mobileLocalDigits, customerUuid } = useAppSelector(selectUniqueCodeDraft);

  const [choice, setChoice] = useState<DevRegAnswer | null>(
    () => readDevRegAnswers()?.registered ?? null
  );
  const navigateAfterCheckRef = useRef(false);

  useEffect(() => {
    if (!navigateAfterCheckRef.current) return;
    navigateAfterCheckRef.current = false;
    navigate('/tradein');
  }, [choice, navigate]);

  const pick = (v: DevRegAnswer) => {
    setChoice((prev) => {
      if (prev === v) return prev;
      writeDevRegAnswers({ registered: v });
      navigateAfterCheckRef.current = true;
      return v;
    });
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!isSmokerProfileComplete()) {
    return <Navigate to="/smokerprofile" replace />;
  }
  if (!isQ7_1Complete()) {
    return <Navigate to="/7_1" replace />;
  }
  if (!isQ7_2StepComplete(codeBrand)) {
    return <Navigate to="/7_2" replace />;
  }
  if (!isQ7_3Complete()) {
    return <Navigate to="/7_3" replace />;
  }
  if (!isQ7_4CompleteForSurvey()) {
    return <Navigate to="/7_4" replace />;
  }
  if (!isQ7_5Complete()) {
    return <Navigate to="/7_5" replace />;
  }
  if (!isQ7_6Complete()) {
    return <Navigate to="/7_6" replace />;
  }

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col justify-center overflow-hidden">
        <div className="mx-auto flex w-full max-w-full flex-col py-6">
          <SurveyBrownPanelInset>
            <SurveyBrownPanel className="!p-4">
              <p className="m-0 text-left text-[16px] font-medium leading-snug text-white/95 sm:text-[17px]">
                {t.devReg.question}
              </p>
              <div className="mt-5 flex flex-col gap-3">
                {OPTION_KEYS.map((opt) => {
                  const id = `devreg-${opt.id}`;
                  const checked = choice === opt.id;
                  return (
                    <label key={opt.id} htmlFor={id} className={checkboxLabelClass}>
                      <input
                        id={id}
                        type="checkbox"
                        checked={checked}
                        onChange={() => pick(opt.id)}
                        className={checkboxClass}
                      />
                      <span>{t.devReg[opt.labelKey]}</span>
                    </label>
                  );
                })}
              </div>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
